import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import type { Tables } from "@/integrations/supabase/types";

export type Instance = Tables<"instances"> & {
  warmup_profiles?: Tables<"warmup_profiles"> | null;
};

export function useInstances() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const instancesQuery = useQuery({
    queryKey: ["instances", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("instances")
        .select("*, warmup_profiles(*)")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as Instance[];
    },
    enabled: !!user?.id,
  });

  const profilesQuery = useQuery({
    queryKey: ["warmup_profiles", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("warmup_profiles")
        .select("*")
        .or(`user_id.is.null,user_id.eq.${user!.id}`);
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const userProfileQuery = useQuery({
    queryKey: ["user_profile", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("max_ports")
        .eq("id", user!.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const createInstance = useMutation({
    mutationFn: async (params: { profile_id: string }) => {
      const { data, error } = await supabase.functions.invoke("create-instance", {
        body: params,
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instances"] });
      toast({ title: "Número adicionado!", description: "Escaneie o QR Code para conectar." });
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao adicionar número", description: err.message, variant: "destructive" });
    },
  });

  const pollQrCode = async (instanceId: string) => {
    const { data, error } = await supabase.functions.invoke("get-qrcode", {
      body: { instance_id: instanceId },
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data as { state: string; qr_code: string | null };
  };

  const disconnectInstance = useMutation({
    mutationFn: async (params: { instance_id: string; action: "disconnect" | "delete" | "restart" }) => {
      const { data, error } = await supabase.functions.invoke("disconnect-instance", {
        body: params,
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["instances"] });
      toast({
        title: vars.action === "delete" ? "Número removido" : "Número desconectado",
      });
    },
    onError: (err: Error) => {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    },
  });

  const updateInstance = useMutation({
    mutationFn: async (params: { id: string; profile_id?: string; is_global_network_active?: boolean; account_type?: string; nickname?: string; is_official_contacts_active?: boolean; is_warmup_groups_active?: boolean }) => {
      const { id, ...updates } = params;
      const { error } = await supabase
        .from("instances")
        .update(updates)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instances"] });
      toast({ title: "Alterações salvas!" });
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" });
    },
  });

  const manageGroupMembership = useMutation({
    mutationFn: async (params: { instance_id: string; action: "join_all" | "leave_all" }) => {
      const { data, error } = await supabase.functions.invoke("manage-group-membership", {
        body: params,
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data as { joined?: number; left?: number; errors?: number; skipped?: number; total: number };
    },
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["instances"] });
      if (vars.action === "join_all") {
        toast({
          title: "Grupos de aquecimento ativados!",
          description: `${data.joined || 0} grupos entrou, ${data.skipped || 0} já participava.`,
        });
      } else {
        toast({
          title: "Grupos de aquecimento desativados!",
          description: `Saiu de ${data.left || 0} grupos.`,
        });
      }
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao gerenciar grupos", description: err.message, variant: "destructive" });
    },
  });

  const syncContacts = useMutation({
    mutationFn: async (params: { instance_id: string }) => {
      const { data, error } = await supabase.functions.invoke("sync-contacts", {
        body: params,
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data as { imported: number; skipped: number; total: number };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["custom_contacts"] });
      toast({
        title: "Contatos sincronizados!",
        description: `${data.imported} importados, ${data.skipped} já existiam.`,
      });
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao sincronizar contatos", description: err.message, variant: "destructive" });
    },
  });

  return {
    instances: instancesQuery.data || [],
    warmupProfiles: profilesQuery.data || [],
    userProfile: userProfileQuery.data,
    isLoading: instancesQuery.isLoading,
    createInstance,
    pollQrCode,
    disconnectInstance,
    updateInstance,
    manageGroupMembership,
    syncContacts,
  };
}
